import React, { forwardRef, useImperativeHandle, useRef, useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import './SmartListView.scss';
import { useBidirectionalInfiniteScroll, useAutoLoadMore } from '../../utils';
import VirtualizedList from '../VirtualizedList';

const SmartListBase = (props, ref) => {
    const {
        id,
        data = [],
        sections,
        renderItem,
        keyExtractor = (item, index) => index,
        onLoadTop,
        onLoadBottom,
        hasMoreTop = false,
        hasMoreBottom = true,
        offset = 80,
        debounceMs = 300,
        inverted = false,
        horizontal = false,
        ListHeaderComponent,
        ListFooterComponent,
        ListHeaderComponentStyle = {},
        ListFooterComponentStyle = {},
        ItemSeparatorComponent,
        ListEmptyComponent,
        itemHeight = 60,
        columnWrapperStyle = {},
        getItemLayout,
        initialScrollIndex,
        numColumns = 1,
        ListOverlayComponent,
        renderSectionHeader,
        enableVirtualization = true,
        overscan = 5, // Number of items to render outside visible area
        getItemHeight, // Optional function to get dynamic height for each item
        virtualizationThreshold = 50, // Minimum items before virtualization kicks in
        enableAutoLoadMore = true, // Enable auto-load when content doesn't fill viewport
        loading = false, // External loading state for auto-load feature
    } = props;

    const containerRef = useRef(null);

    // Determine if virtualization should be used
    const shouldVirtualize =
        enableVirtualization && !sections && numColumns === 1 && data.length >= virtualizationThreshold;

    const { loadingTop, loadingBottom } = useBidirectionalInfiniteScroll({
        containerRef,
        onLoadTop,
        onLoadBottom,
        hasMoreTop,
        hasMoreBottom,
        offset,
        debounceMs,
        isReversed: inverted,
    });

    useEffect(() => {
        if (initialScrollIndex != null && getItemLayout) {
            const { offset } = getItemLayout(data, initialScrollIndex);
            containerRef.current?.scrollTo({
                top: inverted ? containerRef.current.scrollHeight - offset : offset,
            });
        }
    }, [initialScrollIndex, data, getItemLayout, inverted]);

    // Create a ref object that mimics the SmartListView ref interface for useAutoLoadMore
    const listViewRefForAutoLoad = useMemo(
        () => ({
            current: {
                getScrollContainer: () => containerRef.current,
            },
        }),
        [],
    );

    // Auto-load more when content doesn't fill viewport (handles different screen resolutions)
    // Only enable if onLoadBottom and hasMoreBottom are provided, and enableAutoLoadMore is true
    useAutoLoadMore({
        listRef: enableAutoLoadMore && onLoadBottom && hasMoreBottom ? listViewRefForAutoLoad : { current: null },
        data,
        hasMore: enableAutoLoadMore ? hasMoreBottom : false,
        loadMore: enableAutoLoadMore && onLoadBottom ? onLoadBottom : () => {},
        loading: loading || loadingBottom,
        checkDelay: 100,
        debounceMs,
    });

    useImperativeHandle(ref, () => ({
        scrollToOffset: ({ offset, animated = true }) => {
            containerRef.current?.scrollTo({
                top: offset,
                behavior: animated ? 'smooth' : 'auto',
            });
        },
        scrollToEnd: ({ animated = true } = {}) => {
            containerRef.current?.scrollTo({
                top: inverted ? 0 : containerRef.current.scrollHeight,
                behavior: animated ? 'smooth' : 'auto',
            });
        },
        scrollToIndex: ({ index, animated = true }) => {
            if (data.length > index) {
                const offset = getItemLayout ? getItemLayout(data, index).offset : index * itemHeight;
                containerRef.current?.scrollTo({
                    top: inverted ? containerRef.current.scrollHeight - offset : offset,
                    behavior: animated ? 'smooth' : 'auto',
                });
            }
        },
        scrollToItem: ({ itemKey, animated = true }) => {
            const index = data.findIndex((item, i) => keyExtractor(item, i) === itemKey);
            if (index !== -1) {
                const offset = getItemLayout ? getItemLayout(data, index).offset : index * itemHeight;
                containerRef.current?.scrollTo({
                    top: inverted ? containerRef.current.scrollHeight - offset : offset,
                    behavior: animated ? 'smooth' : 'auto',
                });
            }
        },
        flashScrollIndicators: () => {
            containerRef.current?.classList.add('flash-scroll');
            setTimeout(() => containerRef.current?.classList.remove('flash-scroll'), 1000);
        },
        getScrollContainer: () => containerRef.current,
    }));

    // Renders sectioned list if `sections` is provided
    const renderSectionList = useCallback(() => {
        if (!sections?.length) {
            return ListEmptyComponent ? <>{ListEmptyComponent}</> : null;
        }

        return sections.map((section, sIndex) => {
            const { data: sectionData = [], renderItem: sectionRenderItem, numColumns: sectionColumns = 1 } = section;

            const sectionContent = [];

            if (renderSectionHeader) {
                sectionContent.push(renderSectionHeader({ section }));
            }

            for (let i = 0; i < sectionData.length; i += sectionColumns) {
                const rowItems = sectionData.slice(i, i + sectionColumns);
                sectionContent.push(
                    <div key={`section-${sIndex}-row-${i}`} className="list-row1" style={columnWrapperStyle}>
                        {rowItems.map((item, index) => {
                            const key = keyExtractor(item, i + index);
                            return (
                                <React.Fragment key={key}>
                                    {(sectionRenderItem || renderItem)({ item, index: i + index, section })}
                                    {ItemSeparatorComponent && i + index !== sectionData.length - 1 && (
                                        <>{ItemSeparatorComponent}</>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </div>,
                );
            }

            return sectionContent;
        });
    }, [
        sections,
        renderItem,
        renderSectionHeader,
        ItemSeparatorComponent,
        ListEmptyComponent,
        columnWrapperStyle,
        keyExtractor,
    ]);

    const renderMultiColumnList = useCallback(() => {
        const rows = [];

        for (let i = 0; i < data.length; i += numColumns) {
            const rowItems = data.slice(i, i + numColumns);
            rows.push(
                <div key={`row-${i}`} className="list-row" style={columnWrapperStyle}>
                    {rowItems.map((item, colIndex) => {
                        const index = i + colIndex;
                        const key = keyExtractor(item, index);
                        return (
                            <React.Fragment key={key}>
                                {renderItem({ item, index })}
                                {ItemSeparatorComponent && index !== data.length - 1 && <>{ItemSeparatorComponent}</>}
                            </React.Fragment>
                        );
                    })}
                </div>,
            );
        }

        return rows;
    }, [data, numColumns, columnWrapperStyle, keyExtractor, renderItem, ItemSeparatorComponent]);

    const renderFlatList = useCallback(() => {
        return data.map((item, index) => (
            <React.Fragment key={keyExtractor(item, index)}>
                {renderItem({ item, index })}
                {ItemSeparatorComponent && index !== data.length - 1 && <>{ItemSeparatorComponent}</>}
            </React.Fragment>
        ));
    }, [data, keyExtractor, renderItem, ItemSeparatorComponent]);

    const renderVirtualizedContent = useCallback(() => {
        if (!data.length) {
            return null;
        }

        return (
            <VirtualizedList
                items={data}
                containerRef={containerRef}
                itemHeight={itemHeight}
                overscan={overscan}
                getItemHeight={getItemHeight}
                keyExtractor={keyExtractor}
                renderItem={(item, index, measureItem) => {
                    const content = renderItem({ item, index, measureItem });

                    if (content === null || content === undefined) {
                        return content;
                    }

                    return (
                        <div
                            ref={el => {
                                if (el && measureItem) {
                                    measureItem(index, el.offsetHeight);
                                }
                            }}
                        >
                            {content}
                            {ItemSeparatorComponent && index !== data.length - 1 && <>{ItemSeparatorComponent}</>}
                        </div>
                    );
                }}
            />
        );
    }, [data, containerRef, itemHeight, overscan, getItemHeight, keyExtractor, renderItem, ItemSeparatorComponent]);

    const listContent = useMemo(() => {
        if (sections?.length && ListEmptyComponent.props?.Title === 'Engage with your People') {
            return renderSectionList();
        }

        if (!data.length) {
            return ListEmptyComponent ? <>{ListEmptyComponent}</> : null;
        }

        if (numColumns > 1) {
            return renderMultiColumnList();
        }

        if (shouldVirtualize) {
            return renderVirtualizedContent();
        }

        return renderFlatList();
    }, [
        sections,
        renderSectionList,
        data.length,
        ListEmptyComponent,
        numColumns,
        renderMultiColumnList,
        shouldVirtualize,
        renderVirtualizedContent,
        renderFlatList,
    ]);

    return (
        <div
            ref={containerRef}
            id={id || 'smart-list-view'}
            className={`smart-list-view ${inverted ? 'reversed' : ''} ${horizontal ? 'horizontal' : ''} ${
                numColumns > 1 ? 'multi-column' : ''
            } ${shouldVirtualize ? 'virtualized' : ''}`}
        >
            {inverted && loadingBottom && (
                <div className="list-loader">
                    <div className="loader" />
                </div>
            )}
            {inverted && ListFooterComponent && (
                <div className="list-footer" style={ListFooterComponentStyle}>
                    {ListFooterComponent}
                </div>
            )}

            {listContent}

            {!inverted && ListHeaderComponent && (
                <div className="list-header" style={ListHeaderComponentStyle}>
                    {ListHeaderComponent}
                </div>
            )}
            {!inverted && loadingTop && (
                <div className="list-loader">
                    <div className="loader" />
                </div>
            )}
            {inverted
                ? ListHeaderComponent && (
                      <div className="list-header" style={ListHeaderComponentStyle}>
                          {ListHeaderComponent}
                      </div>
                  )
                : ListFooterComponent && (
                      <div className="list-footer" style={ListFooterComponentStyle}>
                          {ListFooterComponent}
                      </div>
                  )}
            {ListOverlayComponent && <div className="list-overlay-component">{ListOverlayComponent}</div>}
        </div>
    );
};

const SmartListView = forwardRef(SmartListBase);

SmartListBase.propTypes = {
    id: PropTypes.string,
    data: PropTypes.array,
    sections: PropTypes.array,
    renderItem: PropTypes.func.isRequired,
    renderSectionHeader: PropTypes.func,
    keyExtractor: PropTypes.func,
    onLoadTop: PropTypes.func,
    onLoadBottom: PropTypes.func,
    hasMoreTop: PropTypes.bool,
    hasMoreBottom: PropTypes.bool,
    offset: PropTypes.number,
    debounceMs: PropTypes.number,
    inverted: PropTypes.bool,
    horizontal: PropTypes.bool,
    ListHeaderComponent: PropTypes.node,
    ListFooterComponent: PropTypes.node,
    ListHeaderComponentStyle: PropTypes.object,
    ListFooterComponentStyle: PropTypes.object,
    ItemSeparatorComponent: PropTypes.node,
    ListEmptyComponent: PropTypes.node,
    itemHeight: PropTypes.number,
    columnWrapperStyle: PropTypes.object,
    getItemLayout: PropTypes.func,
    initialScrollIndex: PropTypes.number,
    numColumns: PropTypes.number,
    ListOverlayComponent: PropTypes.node,
    enableVirtualization: PropTypes.bool,
    overscan: PropTypes.number,
    getItemHeight: PropTypes.func,
    virtualizationThreshold: PropTypes.number,
    enableAutoLoadMore: PropTypes.bool,
    loading: PropTypes.bool,
};

export default SmartListView;
